/**
 * Language and Appearance.
 * Figma `9229:186996`, `9229:187345`.
 *
 * Language options come from `INlsService.getAvailableLocales`.
 * List `9229:187164`: the title is that language's own writing
 * (`localeNativeName`); the description is the English name
 * (`localeTranslatedName` against `DEFAULT_LOCALE`) and does not follow the
 * current UI locale. System Default's description is the OS locale from
 * `getSystemLocale`, in English. Row metrics stay the settings
 * row (`px-5` / `py-4`); this frame's 16/12 padding is the generic list.
 * Appearance column is `9229:187517`. Preview card `9229:187520` fill is
 * `bg/primary` → `--app-base-white-a90`. Night Mode `9229:187656` writes
 * `dark` / `light`; a `system` preference shows the resolved theme and
 * leaves `system` on the next toggle. Accent (`9229:187572`) is a radio group
 * over the `THEME_ACCENTS` presets writing `IThemeService.setAccent` on click.
 * Text size (`9229:187665`, `9229:187666`) has no platform API, so it is hidden
 * behind `TEXT_SIZE_ENABLED`; the static preview (system-text switch on, fill
 * 50.8%) is kept for when it lands.
 * Preview separator `9229:187543` is inset 74px from the content's left edge
 * (bare value from the vector). Open pill and the slider knob omit backdrop-filter.
 */
import type { ChannelClient } from '@hibro/platform-core';
import type {
  INlsService,
  IThemeService,
  Locale,
  LocaleChange,
  LocalePreference,
  ThemeAccent,
  ThemeChange,
  ThemePreference,
  ThemeType,
} from '@hibro/shared';
import {
  accentPresetColor,
  DEFAULT_LOCALE,
  LOCALE_CHANGED_EVENT,
  localeNativeName,
  localeTranslatedName,
  THEME_ACCENTS,
  THEME_CHANGED_EVENT,
} from '@hibro/shared';
import {
  Icon,
  SETTINGS_CARD,
  SETTINGS_HAIRLINE,
  SettingsCheckRow,
  SettingsHairline,
  SettingsPane,
  SettingsSectionTitle,
  SettingsSwitchRow,
  toast,
} from '@hibro/ui-client';
import {
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';

import { settingsWindowLogError } from './log';

/** `9229:187520` bg/primary, not the 80% `SETTINGS_CARD` fill. */
const APPEARANCE_SETTINGS_CARD =
  'w-full overflow-hidden rounded-[14px] bg-(--app-base-white-a90) dark:bg-(--app-color-card)';

const PREVIEW_AVATAR = new URL(
  './assets/appearance-avatar.png',
  import.meta.url,
).href;
const PREVIEW_STICKER = new URL(
  './assets/appearance-sticker.png',
  import.meta.url,
).href;
const PREVIEW_BOT = new URL('./assets/appearance-bot.png', import.meta.url)
  .href;
/** Text size has no platform API yet; hidden until it does. */
const TEXT_SIZE_ENABLED = false;
/** `9229:187674` is 508×4, `preserveAspectRatio="none"`. */
const PREVIEW_TICKS = new URL('./assets/appearance-ticks.svg', import.meta.url)
  .href;

function failureReason(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

export function LanguagePage({
  t,
  nlsService,
  client,
}: {
  t: (key: string) => string;
  nlsService?: INlsService;
  client?: ChannelClient;
}): ReactNode {
  const [preference, setPreference] = useState<LocalePreference | null>(null);
  const [uiLocale, setUiLocale] = useState<Locale | null>(null);
  const [systemLocale, setSystemLocale] = useState<Locale | undefined>(
    undefined,
  );
  const [available, setAvailable] = useState<readonly Locale[]>([]);
  const live = Boolean(nlsService && client);

  useEffect(() => {
    if (!nlsService || !client) {
      return;
    }
    let active = true;
    let ticket = 0;
    const load = () => {
      const current = ++ticket;
      void Promise.all([
        nlsService.getPreference(),
        nlsService.getLocale(),
        nlsService.getSystemLocale(),
        nlsService.getAvailableLocales(),
      ])
        .then(([pref, locale, system, locales]) => {
          if (active && current === ticket) {
            setPreference(pref);
            setUiLocale(locale);
            setSystemLocale(system);
            setAvailable(locales);
          }
        })
        .catch((err: unknown) => {
          settingsWindowLogError('settings language load failed', {
            reason: failureReason(err),
          });
        });
    };
    load();
    const dispose = client.listen(LOCALE_CHANGED_EVENT, (data) => {
      const change = data as LocaleChange;
      const current = ++ticket;
      setPreference(change.preference);
      setUiLocale(change.locale);
      void nlsService
        .getSystemLocale()
        .then((system) => {
          if (active && current === ticket) {
            setSystemLocale(system);
          }
        })
        .catch((err: unknown) => {
          settingsWindowLogError(
            'settings language system locale load failed',
            {
              reason: failureReason(err),
            },
          );
        });
    });
    return () => {
      active = false;
      dispose();
    };
  }, [nlsService, client]);

  const choose = (next: LocalePreference) => {
    if (!nlsService) {
      return;
    }
    const previous = preference;
    setPreference(next);
    void nlsService.setPreference(next).catch((err: unknown) => {
      settingsWindowLogError('settings language save failed', {
        reason: failureReason(err),
      });
      setPreference(previous);
    });
  };

  const rows: { id: LocalePreference; label: string; subtitle?: string }[] =
    live && uiLocale
      ? [
          {
            id: 'system',
            label: t('settings.language.system'),
            subtitle: systemLocale
              ? localeTranslatedName(systemLocale, DEFAULT_LOCALE)
              : undefined,
          },
          ...available.map((locale) => ({
            id: locale,
            label: localeNativeName(locale),
            subtitle: localeTranslatedName(locale, DEFAULT_LOCALE),
          })),
        ]
      : [{ id: 'system', label: t('settings.language.system') }];

  return (
    <SettingsPane>
      <div className={SETTINGS_CARD}>
        {rows.map((item, index) => (
          <SettingsCheckRow
            key={item.id}
            label={item.label}
            subtitle={item.subtitle}
            stacked
            checked={live && preference === item.id}
            disabled={!live}
            last={index === rows.length - 1}
            onClick={() => choose(item.id)}
          />
        ))}
      </div>
    </SettingsPane>
  );
}

export function AppearancePage({
  t,
  themeService,
  client,
}: {
  t: (key: string, args?: Record<string, unknown>) => string;
  themeService?: IThemeService;
  client?: ChannelClient;
}): ReactNode {
  const [preference, setPreference] = useState<ThemePreference | null>(null);
  const [resolved, setResolved] = useState<ThemeType | null>(null);
  const [accent, setAccent] = useState<ThemeAccent | null>(null);
  const live = Boolean(themeService && client);

  useEffect(() => {
    if (!themeService || !client) {
      return;
    }
    let active = true;
    void Promise.all([
      themeService.getPreference(),
      themeService.getTheme(),
      themeService.getAccent(),
    ])
      .then(([pref, theme, currentAccent]) => {
        if (active) {
          setPreference(pref);
          setResolved(theme.type);
          setAccent(currentAccent);
        }
      })
      .catch((err: unknown) => {
        settingsWindowLogError('settings appearance preference load failed', {
          reason: failureReason(err),
        });
      });
    const dispose = client.listen(THEME_CHANGED_EVENT, (data) => {
      const change = data as ThemeChange;
      setPreference(change.preference);
      setResolved(change.theme.type);
      setAccent(change.accent);
    });
    return () => {
      active = false;
      dispose();
    };
  }, [themeService, client]);

  const choose = (next: ThemePreference) => {
    if (!themeService) {
      return;
    }
    const previousPreference = preference;
    const previousResolved = resolved;
    setPreference(next);
    if (next === 'light' || next === 'dark') {
      setResolved(next);
    }
    void themeService.setPreference(next).catch((err: unknown) => {
      settingsWindowLogError('settings appearance preference save failed', {
        reason: failureReason(err),
      });
      setPreference(previousPreference);
      setResolved(previousResolved);
    });
  };

  const chooseAccent = (next: ThemeAccent) => {
    if (!themeService || next === accent) {
      return;
    }
    const previous = accent;
    setAccent(next);
    void themeService.setAccent(next).catch((err: unknown) => {
      const message = failureReason(err);
      settingsWindowLogError('settings appearance accent save failed', {
        accent: next,
        reason: message,
      });
      toast(t('settingsWindow.saveFailed', { message }));
      setAccent(previous);
    });
  };

  return (
    <SettingsPane className="gap-0">
      <SettingsSectionTitle>
        {t('settingsWindow.appearance.preview')}
      </SettingsSectionTitle>
      <div className="flex w-full flex-col gap-3.5">
        <PreviewCard openLabel={t('settingsWindow.appearance.open')}>
          <AccentPicker
            t={t}
            type={resolved}
            accent={accent}
            disabled={!live}
            onChoose={chooseAccent}
          />
        </PreviewCard>
        <div className={APPEARANCE_SETTINGS_CARD}>
          <SettingsSwitchRow
            label={t('settingsWindow.appearance.night')}
            checked={resolved === 'dark'}
            disabled={!live || resolved === null}
            last
            onChange={(on) => choose(on ? 'dark' : 'light')}
          />
        </div>
        {TEXT_SIZE_ENABLED ? (
          <div className="flex w-full flex-col">
            <SettingsSectionTitle>
              {t('settingsWindow.appearance.textSize')}
            </SettingsSectionTitle>
            <div className={APPEARANCE_SETTINGS_CARD}>
              <div className="flex min-h-[54px] w-full items-center justify-between gap-3 px-5 py-4">
                <span className="text-[14px] leading-[22px] text-(--app-color-text-primary)">
                  {t('settingsWindow.appearance.systemText')}
                </span>
                <span
                  role="switch"
                  aria-checked="true"
                  aria-disabled="true"
                  aria-label={t('settingsWindow.appearance.systemText')}
                  className="relative h-5 w-11 shrink-0 rounded-full bg-(--app-base-brand) p-0.5"
                >
                  <span className="block h-full w-[26px] translate-x-[14px] rounded-full bg-(--app-base-white) shadow-[0_0_1px_0_rgba(0,0,0,0.05),0_0_4px_0_rgba(0,0,0,0.05),0_0_44px_0_rgba(0,0,0,0.1)]" />
                </span>
              </div>
              <SettingsHairline />
              <TextSizeSlider />
            </div>
          </div>
        ) : null}
      </div>
    </SettingsPane>
  );
}

function PreviewCard({
  openLabel,
  children,
}: {
  openLabel: string;
  children: ReactNode;
}): ReactNode {
  return (
    <div className={APPEARANCE_SETTINGS_CARD}>
      <div className="flex flex-col px-3.5 py-1.5">
        {/* `9229:187523`: fixed h-80 with centered overflow puts the row 7px
            below the top and the separator 3px into the next item. */}
        <div className="flex h-20 flex-col justify-center gap-2.5 pt-2.5">
          <div className="flex items-center gap-3.5">
            <img
              alt=""
              src={PREVIEW_AVATAR}
              width={52}
              height={52}
              className="size-[52px] shrink-0 rounded-full object-cover"
            />
            <div className="flex min-w-0 flex-1 flex-col">
              <div className="flex items-center justify-between">
                <p className="truncate text-[15px] leading-6 font-medium text-(--app-color-text-primary)">
                  The API Alliance
                </p>
                <span className="shrink-0 text-[12px] leading-normal text-(--app-color-text-quaternary)">
                  10:09
                </span>
              </div>
              <div className="flex flex-col gap-0.5">
                <p className="text-[13px] leading-5 font-medium text-(--app-color-text-primary)">
                  You:
                </p>
                <div className="flex items-center gap-0.5">
                  <span className="relative size-[18px] shrink-0 overflow-hidden rounded-[5px]">
                    <img
                      alt=""
                      src={PREVIEW_STICKER}
                      width={360}
                      height={360}
                      className="absolute top-[-27.22%] left-[-27.78%] size-[155.56%] max-w-none"
                    />
                  </span>
                  <p className="min-w-0 flex-1 truncate text-[13px] leading-5 text-(--app-color-text-tertiary)">
                    Welcome to our platform...
                  </p>
                  <span className="flex shrink-0 items-center gap-1">
                    <span className="flex size-[18px] items-center justify-center text-(--app-base-brand)">
                      {/* `9229:187542` @ stroke is brand, so it follows the live accent. */}
                      <Icon
                        spec={{ type: 'builtin', id: 'i-mention' }}
                        size={18}
                      />
                    </span>
                    <span className="flex size-[18px] items-center justify-center rounded-full bg-(--app-base-brand) text-[11px] leading-none text-(--app-color-on-accent)">
                      2
                    </span>
                  </span>
                </div>
              </div>
            </div>
          </div>
          <span className="relative h-0 w-full shrink-0">
            <span
              className={`absolute top-[-0.5px] right-0 left-[74px] border-t ${SETTINGS_HAIRLINE}`}
            />
          </span>
        </div>
        <div className="flex items-center gap-3.5 pt-2.5 pb-2.5">
          <span className="flex size-[52px] shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#8b63fd]">
            <img
              alt=""
              src={PREVIEW_BOT}
              width={52}
              height={52}
              className="size-full object-cover"
            />
          </span>
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <div className="flex items-center gap-2.5">
              <p className="min-w-0 flex-1 truncate text-[15px] leading-6 font-medium text-(--app-color-text-primary)">
                Chat_Guard_Bot
              </p>
              <span className="w-[23px] shrink-0 text-right text-[11px] leading-4 text-(--app-color-text-quaternary)">
                10:12
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="min-w-0 flex-1 text-[13px] leading-[18px] text-(--app-color-text-tertiary)">
                <p>OK. Send me the</p>
                <p className="truncate">People will see this desc...</p>
              </div>
              <span className="flex h-6 w-[49px] shrink-0 items-center justify-center rounded-full border-[0.8px] border-(--app-base-white-a60) bg-(--app-base-brand) text-[12px] leading-[18px] text-(--app-color-on-accent) shadow-[0_2px_6px_0_rgba(0,0,0,0.04)]">
                {openLabel}
              </span>
            </div>
          </div>
        </div>
      </div>
      {children}
    </div>
  );
}

const ACCENT_KEY_STEP: Partial<Record<string, number>> = {
  ArrowRight: 1,
  ArrowDown: 1,
  ArrowLeft: -1,
  ArrowUp: -1,
};

/**
 * `9229:187572` accent swatches as a radio group (roving tabindex; arrows move
 * and select, like native radios). Each swatch shows its preset's color for the
 * current mode — `brand` is the default brand color, not the live accent — and
 * the selection ring is drawn in the live accent (`--app-base-brand`). Renders
 * no swatches until the theme type + accent are known.
 */
function AccentPicker({
  t,
  type,
  accent,
  disabled,
  onChoose,
}: {
  t: (key: string) => string;
  type: ThemeType | null;
  accent: ThemeAccent | null;
  disabled: boolean;
  onChoose: (accent: ThemeAccent) => void;
}): ReactNode {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const ready = type !== null && accent !== null;

  const onKeyDown = (event: KeyboardEvent, index: number) => {
    const count = THEME_ACCENTS.length;
    let next: number | undefined;
    const step = ACCENT_KEY_STEP[event.key];
    if (step !== undefined) {
      next = (index + step + count) % count;
    } else if (event.key === 'Home') {
      next = 0;
    } else if (event.key === 'End') {
      next = count - 1;
    }
    if (next === undefined) {
      return;
    }
    event.preventDefault();
    refs.current[next]?.focus();
    onChoose(THEME_ACCENTS[next]);
  };

  return (
    <div
      role="radiogroup"
      aria-label={t('settingsWindow.appearance.accent')}
      aria-disabled={disabled || undefined}
      className="flex min-h-[60px] items-center gap-2.5 border-t-[1.5px] border-(--app-base-black-a4) p-4 dark:border-(--app-base-white-a4)"
    >
      {ready
        ? THEME_ACCENTS.map((id, index) => {
            const checked = id === accent;
            return (
              <button
                key={id}
                ref={(el) => {
                  refs.current[index] = el;
                }}
                type="button"
                role="radio"
                aria-checked={checked}
                aria-label={t(`settingsWindow.appearance.accentOption.${id}`)}
                tabIndex={checked ? 0 : -1}
                disabled={disabled}
                onClick={() => onChoose(id)}
                onKeyDown={(event) => onKeyDown(event, index)}
                className="relative flex size-7 cursor-pointer items-center justify-center rounded-full p-0.5 outline-none hover:ring-2 hover:ring-(--app-base-black-a20) focus-visible:ring-2 focus-visible:ring-(--app-color-brand-focus) active:ring-(--app-base-black-a40) disabled:cursor-default disabled:hover:ring-0 disabled:active:ring-0 dark:hover:ring-(--app-base-white-a40) dark:active:ring-(--app-base-white-a50)"
              >
                {checked ? (
                  <span className="absolute inset-0 rounded-full border-[1.5px] border-(--app-base-brand)" />
                ) : null}
                <span
                  className="size-6 rounded-full"
                  style={{ background: accentPresetColor(id, type) }}
                />
              </button>
            );
          })
        : null}
    </div>
  );
}

/** Static `9229:187666`. Fill width 50.8% and track `rgba(120,120,120,0.2)` are bare. */
function TextSizeSlider(): ReactNode {
  return (
    <div
      className="flex w-full items-center gap-1 px-4 py-3"
      aria-hidden="true"
    >
      <span className="flex size-6 items-center justify-center text-[15px] leading-none font-medium text-(--app-color-text-tertiary)">
        A
      </span>
      <div className="relative h-[33px] min-w-0 flex-1">
        <div className="absolute top-1/2 right-0 left-0 h-[6px] -translate-y-1/2 rounded-[3px] bg-[rgba(120,120,120,0.2)]" />
        <div className="absolute top-1/2 left-0 h-[6px] w-[50.8%] -translate-y-1/2 rounded-[3px] bg-(--app-base-brand)" />
        <img
          alt=""
          src={PREVIEW_TICKS}
          width={508}
          height={4}
          className="pointer-events-none absolute top-[calc(50%+9px)] left-4 block h-1 w-[calc(100%-32px)]"
        />
        <span className="absolute top-1/2 left-[50.8%] h-7 w-[57px] -translate-x-1/2 -translate-y-1/2 rounded-full border-[0.5px] border-[#ccc] bg-(--app-base-white) shadow-[0_2px_6px_0_rgba(0,0,0,0.12)]" />
      </div>
      <span className="flex size-6 items-center justify-center text-[22px] leading-none font-semibold text-(--app-color-text-secondary)">
        A
      </span>
    </div>
  );
}
